import React from "react";
import { Helmet } from "react-helmet-async";
import * as Sentry from "@sentry/react";
import {
  createRoutesFromChildren,
  useLocation,
  useNavigationType,
} from "react-router";
import { matchRoutes } from "react-router-dom";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { events } from "@planzone/events";
import {
  useAuthentication,
  usePlanzonesRetrieval,
  useRetrieveCurrentPlanzone,
  useRetrievePlanzoneRole,
  useRetrieveProjectPortfolios,
  useRetrieveUnReadNotificationsNbr,
  useRolesRetrieval,
  useRetrieveProjectsRoles,
  useUserPreferencesRetrieval,
  useRetrievePlanzoneSettings,
  useRetrieveAccessRestrictions,
  useRetrieveStrategicAxes,
  useRetrieveWbsConfiguration,
  useRetrieveSecondaryCriteriaTemplate,
  useRetrieveProjectListingConfiguration,
  useRetrieveResourcesListingConfiguration,
  useRetrieveTimeRecordsListingConfiguration,
  useRetrieveProjectMembersListingConfiguration,
  useRetrieveProjectElementDetailsConfiguration,
  useRetrieveResourceDetailsConfiguration,
  useRetrieveDocuments,
  useRetrievePrivateDocuments,
  useProjectStatusesRetrieval,
  useRetrieveConstraints,
} from "@planzone/view-models";
import { useMessage } from "@components/useMessage";
import { LogoLoader } from "@components/logoLoader";
import { Notifications } from "@scenes/appNotifications/notifications";
import { UnExpectedErrorPage } from "@pages/unExpectedErrorPage";
import { Router } from "./router";
import { LanguageWatcher } from "./languageWatcher";
import { ProjectHistory } from "./projectHistory";
import { Props } from "./types";
import favicon from "./assets/favicon.ico";

// ---------------------------------------------------------------------------
// Why this file is shaped the way it is (profiler, 13-07-2026 sessions):
//
// The old App called ~25 view-model hooks whose results its JSX never used —
// they existed only to fire the initial retrieve* effects. Each hook is a
// store subscription, so ANY notification to any of them re-rendered the
// application root, and a root re-render is a full ~10K-fiber synchronous
// pass (the 300–450ms freezes; profiler origins: App, hooks 37/137/202, for
// both pagination waves and pure-scroll passes).
//
// All of that orchestration now lives in <AppBootstrap/>, which renders
// null. The subscriptions still fire exactly as before — but a re-render of
// a null subtree costs ~nothing and invalidates nobody. App itself keeps
// only hooks whose values its JSX actually renders.
//
// Behavior is unchanged: every effect body and its dependency array is
// verbatim from the old App, in the same relative order.
// ---------------------------------------------------------------------------

const env = {
  "web.testing.planzone7.f10web.com": "testing",
  "web.staging.planzone7.f10web.com": "staging",
  "web.preprod.planzone7.com": "preprod",
  "web.planzone7.com": "prod",
  localhost: "local",
};
const currentEnv = env[window.location.hostname as keyof typeof env] || "local";

function useAppLoad() {
  const initialState = {
    hasLoadedPlanzone: false,
    hasLoadedRole: false,
    hasLoadedRoles: false,
    hasLoadedProjectsRoles: false,
    hasLoadedPreferences: false,
    hasLoadedAccessRestrictions: false,
  };
  const [state, setState] = React.useState(initialState);
  const { hasSession, hasRetrievedSession } = useAuthentication();
  const isAppLoaded = () =>
    state.hasLoadedPlanzone &&
    state.hasLoadedRole &&
    state.hasLoadedRoles &&
    state.hasLoadedProjectsRoles &&
    hasRetrievedSession &&
    state.hasLoadedAccessRestrictions &&
    state.hasLoadedPreferences;

  useMessage((message) =>
    message.type === events.changePlanzoneSucceeded
      ? setState({
          ...initialState,
          hasLoadedPreferences: state.hasLoadedPreferences,
          hasLoadedPlanzone: true,
        })
      : message.type === events.retrievePlanzoneRoleSucceeded ||
          message.type === events.retrievePlanzoneRoleFailed
        ? setState((state) => ({ ...state, hasLoadedRole: true }))
        : message.type === events.retrieveRolesSucceeded ||
            message.type === events.retrieveRolesFailed
          ? setState((state) => ({ ...state, hasLoadedRoles: true }))
          : message.type === events.retrieveCurrentPlanzoneSucceeded ||
              message.type === events.retrieveCurrentPlanzoneFailed
            ? setState((state) => ({ ...state, hasLoadedPlanzone: true }))
            : message.type === events.retrieveUserPreferencesSucceeded ||
                message.type === events.retrieveUserPreferencesFailed
              ? setState((state) => ({
                  ...state,
                  hasLoadedPreferences: true,
                }))
              : message.type === events.retrieveAccessRestrictionsSucceeded ||
                  message.type === events.retrieveAccessRestrictionsFailed
                ? setState((state) => ({
                    ...state,
                    hasLoadedAccessRestrictions: true,
                  }))
                : (message.type === events.retrieveProjectsRolesSucceeded ||
                    message.type === events.retrieveProjectsRolesFailed) &&
                  setState((state) => ({
                    ...state,
                    hasLoadedProjectsRoles: true,
                  })),
  );

  return (!hasSession && hasRetrievedSession) || isAppLoaded();
}

function Wrapper({
  children,
}: {
  children: (isLoaded: boolean) => React.ReactNode;
}) {
  const isAppLoaded = useAppLoad();

  return <>{children(isAppLoaded)}</>;
}

function useQuery() {
  return new URLSearchParams(
    window.location.search || window.location.hash.replace(/^#/, "?"),
  );
}

function isRegistrationConfirmationPage() {
  return window.location.pathname === "/registration/confirmation";
}

function extractKeys(url: URL) {
  return Object.entries(
    url.search
      .replace(/^\?/, "")
      .split("&")
      .filter((part) => !!part)
      .reduce((keys, entry) => {
        const [key, value] = entry.split("=");

        return { ...keys, [key]: value };
      }, {}),
  )
    .filter(([key, value]) => key !== "code" && key !== "state")
    .reduce((keys, [key, value]) => ({ ...keys, [key]: value }), {});
}

function clearUrl() {
  const url = new URL(window.location.href);
  const keys = extractKeys(url);
  const queries = Object.entries(keys).reduce(
    (url, [key, value]) => `${url}${url.length > 0 ? "&" : ""}${key}=${value}`,
    "",
  );

  history.replaceState(
    {},
    window.document.title,
    `${window.location.href.replace(/\?(.)+/, "")}${
      !!queries ? "?" : ""
    }${queries}`,
  );
}

/**
 * Renders null. Owns every data-loading subscription and kick-off effect the
 * old App held. When any of these hooks' stores notify, the re-render is
 * contained here instead of cascading from the application root.
 */
function AppBootstrap() {
  const query = useQuery();
  const { retrievePlanzones } = usePlanzonesRetrieval();
  const { authenticate, hasSession, account, hasRetrievedSession } =
    useAuthentication();
  const { retrieveCurrentPlanzone, planzone } = useRetrieveCurrentPlanzone();
  const {
    retrievePlanzoneRole,
    isRequestSuccess,
    isRequestPending,
    isRequestFailure,
  } = useRetrievePlanzoneRole();
  const {
    isRequestSuccess: isPlanzoneSettingsRequestSuccess,
    isRequestPending: isPlanzoneSettingsRequestPending,
    isRequestFailure: isPlanzoneSettingsRequestFailure,
    settings,
    retrievePlanzoneSettings,
  } = useRetrievePlanzoneSettings(planzone);
  const {
    retrieveProjectPortfolios,
    isRequestSuccess: isProjectPortfoliosRequestSuccess,
    isRequestPending: isProjectPortfoliosRequestPending,
    isRequestFailure: isProjectPortfoliosRequestFailure,
  } = useRetrieveProjectPortfolios();
  const {
    retrieveRoles,
    isRequestSuccess: isRolesRequestSuccess,
    isRequestPending: isRolesRequestPending,
    isRequestFailure: isRolesRequestFailure,
  } = useRolesRetrieval();
  const {
    isRequestFailure: isRetrieveAccessRestrictionsRequestFailure,
    isRequestSuccess: isRetrieveAccessRestrictionsRequestSuccess,
    isRequestPending: isRetrieveAccessRestrictionsRequestPending,
    retrieveAccessRestrictions,
  } = useRetrieveAccessRestrictions();
  const {
    retrieveUnReadNotificationsNbr,
    isRequestSuccess: isRetrieveUnReadNotificationsNbrRequestSuccess,
    isRequestPending: isRetrieveUnReadNotificationsNbrRequestPending,
    isRequestFailure: isRetrieveUnReadNotificationsNbrRequestFailure,
  } = useRetrieveUnReadNotificationsNbr();
  const {
    retrieveProjectsRoles,
    isRequestSuccess: isRetrieveProjectsRolesRequestSuccess,
    isRequestPending: isRetrieveProjectsRolesRequestPending,
    isRequestFailure: isRetrieveProjectsRolesRequestFailure,
  } = useRetrieveProjectsRoles();
  const {
    retrieveUserPreferences,
    isRequestSuccess: isRetrievePreferencesRequestSuccess,
    isRequestPending: isRetrievePreferencesRequestPending,
    isRequestFailure: isRetrievePreferencesRequestFailure,
  } = useUserPreferencesRetrieval();
  const {
    retrieveStrategicAxes,
    isRequestSuccess: isRetrieveStrategicAxesRequestSuccess,
    isRequestPending: isRetrieveStrategicAxesRequestPending,
    isRequestFailure: isRetrieveStrategicAxesRequestFailure,
  } = useRetrieveStrategicAxes();
  const {
    retrieveSecondaryCriteriaTemplate:
      retrieveProjectElementsSecondaryCriteriaTemplate,
    isRequestSuccess:
      isRetrieveProjectElementsSecondaryCriteriaTemplateRequestSuccess,
    isRequestPending:
      isRetrieveProjectElementsSecondaryCriteriaTemplateRequestPending,
    isRequestFailure:
      isRetrieveProjectElementsSecondaryCriteriaTemplateRequestFailure,
  } = useRetrieveSecondaryCriteriaTemplate(planzone.uuid, "projectElement");
  const {
    retrieveSecondaryCriteriaTemplate:
      retrieveProjectsSecondaryCriteriaTemplate,
    isRequestSuccess: isRetrieveProjectsSecondaryCriteriaTemplateRequestSuccess,
    isRequestPending: isRetrieveProjectsSecondaryCriteriaTemplateRequestPending,
    isRequestFailure: isRetrieveProjectsSecondaryCriteriaTemplateRequestFailure,
  } = useRetrieveSecondaryCriteriaTemplate(planzone.uuid, "project");
  const {
    retrieveSecondaryCriteriaTemplate:
      retrieveResourcesSecondaryCriteriaTemplate,
    isRequestSuccess:
      isRetrieveResourcesSecondaryCriteriaTemplateRequestSuccess,
    isRequestPending:
      isRetrieveResourcesSecondaryCriteriaTemplateRequestPending,
    isRequestFailure:
      isRetrieveResourcesSecondaryCriteriaTemplateRequestFailure,
  } = useRetrieveSecondaryCriteriaTemplate(planzone.uuid, "resource");
  const {
    retrieveWbsConfiguration,
    isRequestSuccess: isRetrieveWbsConfigurationRequestSuccess,
    isRequestPending: isRetrieveWbsConfigurationRequestPending,
    isRequestFailure: isRetrieveWbsConfigurationRequestFailure,
  } = useRetrieveWbsConfiguration(planzone);
  const {
    retrieveProjectElementDetailsConfiguration,
    isRequestSuccess:
      isRetrieveProjectElementDetailsConfigurationRequestSuccess,
    isRequestPending:
      isRetrieveProjectElementDetailsConfigurationRequestPending,
    isRequestFailure:
      isRetrieveProjectElementDetailsConfigurationRequestFailure,
  } = useRetrieveProjectElementDetailsConfiguration(planzone);
  const {
    retrieveResourceDetailsConfiguration,
    isRequestSuccess: isRetrieveResourceDetailsConfigurationRequestSuccess,
    isRequestPending: isRetrieveResourceDetailsConfigurationRequestPending,
    isRequestFailure: isRetrieveResourceDetailsConfigurationRequestFailure,
  } = useRetrieveResourceDetailsConfiguration(planzone);
  const {
    retrieveProjectListingConfiguration,
    isRequestSuccess: isRetrieveProjectListingConfigurationRequestSuccess,
    isRequestPending: isRetrieveProjectListingConfigurationRequestPending,
    isRequestFailure: isRetrieveProjectListingConfigurationRequestFailure,
  } = useRetrieveProjectListingConfiguration(planzone);
  const {
    retrieveResourcesListingConfiguration,
    isRequestSuccess: isRetrieveResourcesListingConfigurationRequestSuccess,
    isRequestPending: isRetrieveResourcesListingConfigurationRequestPending,
    isRequestFailure: isRetrieveResourcesListingConfigurationRequestFailure,
  } = useRetrieveResourcesListingConfiguration(planzone);
  const {
    retrieveTimeRecordsListingConfiguration,
    isRequestSuccess: isRetrieveTimeRecordsListingConfigurationRequestSuccess,
    isRequestPending: isRetrieveTimeRecordsListingConfigurationRequestPending,
    isRequestFailure: isRetrieveTimeRecordsListingConfigurationRequestFailure,
  } = useRetrieveTimeRecordsListingConfiguration(planzone);
  const {
    retrieveProjectMembersListingConfiguration,
    isRequestSuccess:
      isRetrieveProjectMembersListingConfigurationRequestSuccess,
    isRequestPending:
      isRetrieveProjectMembersListingConfigurationRequestPending,
    isRequestFailure:
      isRetrieveProjectMembersListingConfigurationRequestFailure,
  } = useRetrieveProjectMembersListingConfiguration(planzone);
  const {
    isRequestPending: isRetrieveDocumentsRequestPending,
    isRequestSuccess: isRetrieveDocumentsRequestSuccess,
    isRequestFailure: isRetrieveDocumentsRequestFailure,
    retrieveDocuments,
  } = useRetrieveDocuments(planzone.uuid, "planzone");
  const {
    isRequestPending: isRetrievePrivateDocumentsRequestPending,
    isRequestSuccess: isRetrievePrivateDocumentsRequestSuccess,
    isRequestFailure: isRetrievePrivateDocumentsRequestFailure,
    retrievePrivateDocuments,
  } = useRetrievePrivateDocuments(planzone.uuid, "planzone");
  const {
    isRequestPending: isRetrieveProjectStatusesRequestPending,
    isRequestSuccess: isRetrieveProjectStatusesRequestSuccess,
    isRequestFailure: isRetrieveProjectStatusesRequestFailure,
    retrieveProjectStatuses,
  } = useProjectStatusesRetrieval();
  const {
    isRequestPending: isRetrieveConstraintsRequestPending,
    isRequestSuccess: isRetrieveConstraintsRequestSuccess,
    isRequestFailure: isRetrieveConstraintsRequestFailure,
    retrieveConstraints,
  } = useRetrieveConstraints();

  React.useEffect(() => {
    const code = query.get("code") || "";

    !isRegistrationConfirmationPage() &&
      (code ? authenticate({ code }) : authenticate(null));
  }, []);

  React.useEffect(() => {
    clearUrl();
  }, []);

  React.useEffect(() => {
    hasSession &&
      hasRetrievedSession &&
      !isRetrieveDocumentsRequestPending &&
      !isRetrieveDocumentsRequestSuccess &&
      !isRetrieveDocumentsRequestFailure &&
      retrieveDocuments({ namespace: planzone.uuid, type: "planzone" });
  }, [planzone.uuid]);

  React.useEffect(() => {
    hasSession &&
      hasRetrievedSession &&
      !isRetrievePrivateDocumentsRequestPending &&
      !isRetrievePrivateDocumentsRequestSuccess &&
      !isRetrievePrivateDocumentsRequestFailure &&
      retrievePrivateDocuments({ namespace: planzone.uuid, type: "planzone" });
  }, [planzone.uuid]);

  React.useEffect(() => {
    hasSession && hasRetrievedSession && retrievePlanzones();
  }, [hasSession, hasRetrievedSession]);

  React.useEffect(() => {
    hasSession && hasRetrievedSession && retrieveCurrentPlanzone();
  }, [hasSession, hasRetrievedSession]);

  React.useEffect(() => {
    hasSession &&
      hasRetrievedSession &&
      !isRequestSuccess &&
      !isRequestPending &&
      !isRequestFailure &&
      retrievePlanzoneRole({ account, planzone });
  }, [planzone.uuid, account.uuid]);

  React.useEffect(() => {
    hasSession &&
      hasRetrievedSession &&
      !isRetrieveProjectsRolesRequestSuccess &&
      !isRetrieveProjectsRolesRequestPending &&
      !isRetrieveProjectsRolesRequestFailure &&
      retrieveProjectsRoles(account);
  }, [planzone.uuid, account.uuid]);

  React.useEffect(() => {
    hasSession &&
      hasRetrievedSession &&
      !isRetrieveAccessRestrictionsRequestFailure &&
      !isRetrieveAccessRestrictionsRequestSuccess &&
      !isRetrieveAccessRestrictionsRequestPending &&
      retrieveAccessRestrictions(planzone);
  }, [planzone.uuid]);

  React.useEffect(() => {
    hasSession &&
      hasRetrievedSession &&
      !isProjectPortfoliosRequestSuccess &&
      !isProjectPortfoliosRequestPending &&
      !isProjectPortfoliosRequestFailure &&
      retrieveProjectPortfolios(planzone);
  }, [planzone.uuid]);

  React.useEffect(() => {
    hasSession &&
      hasRetrievedSession &&
      !isRetrieveStrategicAxesRequestSuccess &&
      !isRetrieveStrategicAxesRequestPending &&
      !isRetrieveStrategicAxesRequestFailure &&
      retrieveStrategicAxes(planzone);
  }, [planzone.uuid]);

  React.useEffect(() => {
    hasSession &&
      hasRetrievedSession &&
      !isRolesRequestSuccess &&
      !isRolesRequestPending &&
      !isRolesRequestFailure &&
      retrieveRoles(planzone);
  }, [planzone.uuid]);

  React.useEffect(() => {
    hasSession &&
      hasRetrievedSession &&
      !!planzone.uuid &&
      !isPlanzoneSettingsRequestSuccess &&
      !isPlanzoneSettingsRequestPending &&
      !isPlanzoneSettingsRequestFailure &&
      retrievePlanzoneSettings(planzone);
  }, [planzone.uuid]);

  React.useEffect(() => {
    if (
      (currentEnv === "prod" ||
        currentEnv === "testing" ||
        currentEnv === "preprod") &&
      isPlanzoneSettingsRequestSuccess &&
      settings.allowDataScrapingForDebug
    ) {
      Sentry.init({
        dsn: "https://01ed1c48d7d0d2b0c02fcd67cfdcd88e@o4508800639041536.ingest.de.sentry.io/4508805926551632",
        environment: currentEnv,
        integrations: [
          Sentry.browserTracingIntegration(),
          Sentry.replayIntegration(),
          Sentry.reactRouterV6BrowserTracingIntegration({
            useEffect: React.useEffect,
            useLocation,
            useNavigationType,
            createRoutesFromChildren,
            matchRoutes,
          }),
        ],
        tracesSampleRate: 1.0,
        tracePropagationTargets: [window.location.hostname],
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
      });
    }
  }, [
    planzone.uuid,
    isPlanzoneSettingsRequestSuccess,
    settings.allowDataScrapingForDebug,
  ]);

  React.useEffect(() => {
    hasSession &&
      hasRetrievedSession &&
      !isRetrieveUnReadNotificationsNbrRequestPending &&
      !isRetrieveUnReadNotificationsNbrRequestSuccess &&
      !isRetrieveUnReadNotificationsNbrRequestFailure &&
      retrieveUnReadNotificationsNbr(account.resource);
  }, [planzone.uuid]);

  React.useEffect(() => {
    hasSession &&
      hasRetrievedSession &&
      !isRetrievePreferencesRequestPending &&
      !isRetrievePreferencesRequestSuccess &&
      !isRetrievePreferencesRequestFailure &&
      retrieveUserPreferences(account);
  }, [planzone.uuid]);

  React.useEffect(() => {
    hasSession &&
      hasRetrievedSession &&
      !isRetrieveWbsConfigurationRequestSuccess &&
      !isRetrieveWbsConfigurationRequestPending &&
      !isRetrieveWbsConfigurationRequestFailure &&
      retrieveWbsConfiguration(planzone);
  }, [planzone.uuid]);

  React.useEffect(() => {
    hasSession &&
      hasRetrievedSession &&
      !isRetrieveProjectElementDetailsConfigurationRequestSuccess &&
      !isRetrieveProjectElementDetailsConfigurationRequestPending &&
      !isRetrieveProjectElementDetailsConfigurationRequestFailure &&
      retrieveProjectElementDetailsConfiguration(planzone);
  }, [planzone.uuid]);

  React.useEffect(() => {
    hasSession &&
      hasRetrievedSession &&
      !isRetrieveResourceDetailsConfigurationRequestSuccess &&
      !isRetrieveResourceDetailsConfigurationRequestPending &&
      !isRetrieveResourceDetailsConfigurationRequestFailure &&
      retrieveResourceDetailsConfiguration(planzone);
  }, [planzone.uuid]);

  React.useEffect(() => {
    hasSession &&
      hasRetrievedSession &&
      !isRetrieveProjectListingConfigurationRequestSuccess &&
      !isRetrieveProjectListingConfigurationRequestPending &&
      !isRetrieveProjectListingConfigurationRequestFailure &&
      retrieveProjectListingConfiguration(planzone);
  }, [planzone.uuid]);

  React.useEffect(() => {
    hasSession &&
      hasRetrievedSession &&
      !isRetrieveResourcesListingConfigurationRequestSuccess &&
      !isRetrieveResourcesListingConfigurationRequestPending &&
      !isRetrieveResourcesListingConfigurationRequestFailure &&
      retrieveResourcesListingConfiguration(planzone);
  }, [planzone.uuid]);

  React.useEffect(() => {
    hasSession &&
      hasRetrievedSession &&
      !isRetrieveTimeRecordsListingConfigurationRequestSuccess &&
      !isRetrieveTimeRecordsListingConfigurationRequestPending &&
      !isRetrieveTimeRecordsListingConfigurationRequestFailure &&
      retrieveTimeRecordsListingConfiguration(planzone);
  }, [planzone.uuid]);

  React.useEffect(() => {
    hasSession &&
      hasRetrievedSession &&
      !isRetrieveProjectMembersListingConfigurationRequestSuccess &&
      !isRetrieveProjectMembersListingConfigurationRequestPending &&
      !isRetrieveProjectMembersListingConfigurationRequestFailure &&
      retrieveProjectMembersListingConfiguration(planzone);
  }, [planzone.uuid]);

  React.useEffect(() => {
    hasSession &&
      hasRetrievedSession &&
      !isRetrieveProjectElementsSecondaryCriteriaTemplateRequestSuccess &&
      !isRetrieveProjectElementsSecondaryCriteriaTemplateRequestPending &&
      !isRetrieveProjectElementsSecondaryCriteriaTemplateRequestFailure &&
      retrieveProjectElementsSecondaryCriteriaTemplate({
        namespace: planzone.uuid,
        scope: "planzone",
        elementType: "projectElement",
      });
  }, [planzone.uuid]);

  React.useEffect(() => {
    hasSession &&
      hasRetrievedSession &&
      !isRetrieveProjectsSecondaryCriteriaTemplateRequestSuccess &&
      !isRetrieveProjectsSecondaryCriteriaTemplateRequestPending &&
      !isRetrieveProjectsSecondaryCriteriaTemplateRequestFailure &&
      retrieveProjectsSecondaryCriteriaTemplate({
        namespace: planzone.uuid,
        scope: "planzone",
        elementType: "project",
      });
  }, [planzone.uuid]);

  React.useEffect(() => {
    hasSession &&
      hasRetrievedSession &&
      !isRetrieveResourcesSecondaryCriteriaTemplateRequestSuccess &&
      !isRetrieveResourcesSecondaryCriteriaTemplateRequestPending &&
      !isRetrieveResourcesSecondaryCriteriaTemplateRequestFailure &&
      retrieveResourcesSecondaryCriteriaTemplate({
        namespace: planzone.uuid,
        scope: "planzone",
        elementType: "resource",
      });
  }, [planzone.uuid]);

  React.useEffect(() => {
    hasSession &&
      hasRetrievedSession &&
      !isRetrieveProjectStatusesRequestPending &&
      !isRetrieveProjectStatusesRequestSuccess &&
      !isRetrieveProjectStatusesRequestFailure &&
      retrieveProjectStatuses();
  }, [planzone.uuid]);

  React.useEffect(() => {
    hasSession &&
      hasRetrievedSession &&
      !isRetrieveConstraintsRequestPending &&
      !isRetrieveConstraintsRequestSuccess &&
      !isRetrieveConstraintsRequestFailure &&
      retrieveConstraints();
  }, [
    isRetrieveConstraintsRequestPending,
    isRetrieveConstraintsRequestSuccess,
    isRetrieveConstraintsRequestFailure,
    planzone.uuid,
  ]);

  return null;
}

/**
 * The ErrorBoundary fallback needs the settings flag, but reading it in App
 * would resubscribe App to the settings store. Read it lazily here instead —
 * this only ever renders after a crash.
 */
function AppErrorPage({
  errorData,
}: {
  errorData: Parameters<
    NonNullable<
      Extract<
        React.ComponentProps<typeof Sentry.ErrorBoundary>["fallback"],
        (...args: never[]) => unknown
      >
    >
  >[0];
}) {
  const { planzone } = useRetrieveCurrentPlanzone();
  const { isRequestSuccess, settings } = useRetrievePlanzoneSettings(planzone);

  return (
    <UnExpectedErrorPage
      errorData={errorData}
      debuggerInitialized={
        (currentEnv === "prod" ||
          currentEnv === "testing" ||
          currentEnv === "preprod") &&
        isRequestSuccess &&
        settings.allowDataScrapingForDebug
      }
    />
  );
}

function App(props: Props) {
  // Plain URLSearchParams construction — not a subscription, safe to keep.
  const query = useQuery();

  return (
    <React.Fragment>
      <Helmet>
        <link rel="icon" href={favicon} />
      </Helmet>
      <Sentry.ErrorBoundary
        fallback={(errorData) => <AppErrorPage errorData={errorData} />}
      >
        <AppBootstrap />
        <Wrapper>
          {(isLoaded) => (
            <>
              <LogoLoader>
                <DndProvider backend={HTML5Backend}>
                  <Router queries={query} isAppLoaded={isLoaded} />
                </DndProvider>
              </LogoLoader>
              <Notifications notificationsLifeTimeInMs={6000} />
              <LanguageWatcher />
              <ProjectHistory />
            </>
          )}
        </Wrapper>
      </Sentry.ErrorBoundary>
    </React.Fragment>
  );
}

export { App };
